import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { handleApiError } from '@/lib/api/axios'
import { useCan } from '@/hooks/useCan'
import { useUser } from '@/app/store/auth'
import { mobilisationApi, mobilisationKeys, type MobilisableTeam, type MobilisationMember, type TeamChoice } from '../api'
import { MemberList } from './MemberList'

/**
 * A recovery set's mobilisation of standby users (MFA build plan, Stage 4).
 *
 * This replaces "Activate All Users". Standby users are let in by a request that one
 * administrator makes and a different one approves; each user then enrols with a code sent to
 * their phone, and is let in by a named person - an approver for a team's lead and deputy, the
 * lead or deputy for the team's members. Standing down takes it all away again.
 */
export function MobilisationRequestPanel({ setId }: { setId: number }) {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const me = Number(useUser()?.id ?? 0)
    const mayRequest = useCan('mobilisation.request')
    const mayApprove = useCan('mobilisation.approve')
    const mayConfirm = useCan('mobilisation.confirm')
    const [reason, setReason] = useState('')
    const [endReason, setEndReason] = useState('')
    const [choices, setChoices] = useState<Record<number, { lead?: number; deputy?: number }>>({})

    const query = useQuery({ queryKey: mobilisationKeys.forSet(setId), queryFn: () => mobilisationApi.forSet(setId), refetchInterval: 15_000 })
    const refresh = () => queryClient.invalidateQueries({ queryKey: mobilisationKeys.forSet(setId) })
    const act = useMutation({ mutationFn: (work: () => Promise<unknown>) => work(), onSuccess: () => { setReason(''); setEndReason(''); refresh() } })

    if (query.isLoading) return <p className="text-sm text-muted-foreground">{t('devices.loading')}</p>
    if (query.isError) return <p className="text-sm text-destructive" role="alert">{handleApiError(query.error)}</p>
    const mobilisation = query.data?.mobilisation ?? null
    const teams = query.data?.teams ?? []
    const open = mobilisation && (mobilisation.status === 'pending_approval' || mobilisation.status === 'approved')

    const chosen: TeamChoice[] = Object.entries(choices)
        .filter(([, c]) => c.lead)
        .map(([teamId, c]) => ({ teamId: Number(teamId), leadUserId: c.lead!, deputyUserId: c.deputy ?? null }))
    const toggleTeam = (team: MobilisableTeam) => setChoices((prev) => {
        const next = { ...prev }
        if (next[team.id]) delete next[team.id]; else next[team.id] = {}
        return next
    })
    // Whom this screen offers to decide for: an approver decides for anyone; a lead or deputy who
    // has been let in decides for their own team's members. Never yourself.
    const myTeams = new Set((mobilisation?.members ?? []).filter((m) => m.userId === me && m.role !== 'member' && m.status === 'confirmed').map((m) => m.teamId))
    const mayDecide = (member: MobilisationMember) => member.userId !== me
        && (mayApprove || (mayConfirm && member.role === 'member' && myTeams.has(member.teamId)))

    return (
        <div className="space-y-4" data-testid="mobilisation-panel">
            <div>
                <h1 className="text-xl font-semibold">{t('mobilisation.title')}</h1>
                <p className="mt-0.5 text-sm text-muted-foreground">{t('mobilisation.description')}</p>
            </div>
            {act.isError && <p className="text-sm text-destructive" role="alert">{handleApiError(act.error)}</p>}

            {!open && (
                <div className="space-y-3" data-testid="mobilisation-request-form">
                    {mobilisation && <p className="text-sm text-muted-foreground">{t(`mobilisation.last.${mobilisation.status}`)}</p>}
                    {teams.length === 0 && <p className="text-sm text-muted-foreground">{t('mobilisation.noTeams')}</p>}
                    {teams.map((team) => {
                        const choice = choices[team.id]
                        const reachable = team.users.filter((u) => u.hasNumber)
                        return (
                            <div key={team.id} className="rounded-md border p-3" data-testid="mobilisable-team">
                                <label className="flex items-center gap-2 text-sm font-medium">
                                    <input type="checkbox" checked={Boolean(choice)} onChange={() => toggleTeam(team)} disabled={!mayRequest} />
                                    {team.name}
                                    <span className="font-normal text-muted-foreground">
                                        · {t('mobilisation.standbyCount', { count: team.users.length })}
                                        {reachable.length < team.users.length && <span className="text-destructive"> · {t('mobilisation.unreachableCount', { count: team.users.length - reachable.length })}</span>}
                                    </span>
                                </label>
                                {choice && (
                                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                                        {(['lead', 'deputy'] as const).map((role) => (
                                            <div key={role} className="space-y-1">
                                                <Label htmlFor={`team-${team.id}-${role}`}>{t(role === 'lead' ? 'mobilisation.chooseLead' : 'mobilisation.chooseDeputy')}</Label>
                                                <select
                                                    id={`team-${team.id}-${role}`} className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                                                    value={choice[role] ?? ''}
                                                    onChange={(e) => setChoices((prev) => ({ ...prev, [team.id]: { ...prev[team.id], [role]: e.target.value ? Number(e.target.value) : undefined } }))}
                                                >
                                                    <option value="">{role === 'lead' ? t('mobilisation.choose') : t('mobilisation.noDeputy')}</option>
                                                    {/* Only someone a code can be sent to, and not the same person twice. */}
                                                    {reachable.filter((u) => u.id !== choice[role === 'lead' ? 'deputy' : 'lead']).map((u) => (
                                                        <option key={u.id} value={u.id}>{u.name || u.email}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                    {mayRequest && teams.length > 0 && (
                        <form
                            className="space-y-2"
                            onSubmit={(e) => { e.preventDefault(); if (chosen.length > 0 && reason.trim().length >= 10) act.mutate(() => mobilisationApi.request({ setId, reason: reason.trim(), teams: chosen })) }}
                        >
                            <Label htmlFor="mobilisation-reason">{t('mobilisation.reasonLabel')}</Label>
                            <div className="flex gap-2">
                                <Input id="mobilisation-reason" value={reason} maxLength={255} onChange={(e) => setReason(e.target.value)} />
                                <Button type="submit" disabled={act.isPending || chosen.length === 0 || chosen.length !== Object.keys(choices).length || reason.trim().length < 10}>
                                    {t('mobilisation.ask')}
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">{t('mobilisation.askHint')}</p>
                        </form>
                    )}
                </div>
            )}

            {mobilisation && open && (
                <div className="space-y-3">
                    <div className="rounded-md border p-3 text-sm" data-testid="mobilisation-state" data-status={mobilisation.status}>
                        <p className="font-medium">{t(`mobilisation.state.${mobilisation.status}`)}</p>
                        <p className="text-muted-foreground">{mobilisation.reason}</p>
                        <p className="text-xs text-muted-foreground">
                            {t('mobilisation.askedBy', { name: mobilisation.requestedBy?.name || mobilisation.requestedBy?.email || '' })}
                            {mobilisation.approvedBy ? ` · ${t('mobilisation.approvedBy', { name: mobilisation.approvedBy.name || mobilisation.approvedBy.email })}` : ''}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground" data-testid="mobilisation-counts">
                            {t('mobilisation.counts', { confirmed: mobilisation.summary.confirmed, enrolled: mobilisation.summary.enrolled, invited: mobilisation.summary.invited, waiting: mobilisation.summary.waiting, total: mobilisation.summary.total })}
                            {mobilisation.summary.noNumber > 0 && <span className="text-destructive"> · {t('mobilisation.unreachableCount', { count: mobilisation.summary.noNumber })}</span>}
                        </p>
                    </div>

                    {mobilisation.status === 'pending_approval' && mayApprove && (
                        mobilisation.requestedBy?.id === me ? (
                            <p className="text-sm text-muted-foreground" data-testid="needs-other-approver">{t('mobilisation.needsOtherApprover')}</p>
                        ) : (
                            <div className="flex gap-2">
                                <Button onClick={() => act.mutate(() => mobilisationApi.approve(mobilisation.id))} disabled={act.isPending}>{t('mobilisation.approve')}</Button>
                                <Button variant="outline" onClick={() => act.mutate(() => mobilisationApi.reject(mobilisation.id, 'Rejected by an administrator'))} disabled={act.isPending}>{t('mobilisation.reject')}</Button>
                            </div>
                        )
                    )}

                    <MemberList
                        members={mobilisation.members}
                        mayDecide={(member) => mobilisation.status === 'approved' && mayDecide(member)}
                        busy={act.isPending}
                        onConfirm={(member) => act.mutate(() => mobilisationApi.confirm(member.id))}
                        onRefuse={(member, why) => act.mutate(() => mobilisationApi.refuse(member.id, why))}
                        onResend={(member) => act.mutate(() => mobilisationApi.resend(member.id))}
                    />

                    {mayApprove && (
                        <form
                            className="space-y-2 rounded-md border border-destructive/40 p-3" data-testid="stand-down-form"
                            onSubmit={(e) => { e.preventDefault(); if (endReason.trim().length >= 10) act.mutate(() => mobilisationApi.standDown(mobilisation.id, endReason.trim())) }}
                        >
                            <Label htmlFor="stand-down-reason">{t('mobilisation.standDownLabel')}</Label>
                            <div className="flex gap-2">
                                <Input id="stand-down-reason" value={endReason} maxLength={255} onChange={(e) => setEndReason(e.target.value)} />
                                <Button type="submit" variant="destructive" disabled={act.isPending || endReason.trim().length < 10}>{t('mobilisation.standDown')}</Button>
                            </div>
                            <p className="text-xs text-muted-foreground">{t('mobilisation.standDownHint')}</p>
                        </form>
                    )}
                </div>
            )}
        </div>
    )
}
